import * as React from 'react'
import PropTypes from 'prop-types'

import {
  BLOCKQUOTE_SHORTCUT_KEY,
  useBlockquote,
} from '@tiptap-ui/components/tiptap-ui/blockquote-button'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

// --- Lib ---
import { parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Badge } from '@tiptap-ui/components/tiptap-ui-primitive/badge'

export function BlockquoteShortcutBadge ({
  shortcutKeys = BLOCKQUOTE_SHORTCUT_KEY,
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
}

BlockquoteShortcutBadge.propTypes = {
  shortcutKeys: PropTypes.string,
}

/**
 * Button component for toggling blockquote in a Tiptap editor.
 *
 * For custom button implementations, use the `useBlockquote` hook instead.
 */
export const BlockquoteButton = React.forwardRef((
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onToggled,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canToggle,
    isActive,
    handleToggle,
    label,
    shortcutKeys,
    Icon,
  } = useBlockquote({
    editor,
    hideWhenUnavailable,
    onToggled,
  })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggle()
  }, [handleToggle, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type='button'
      data-style='ghost'
      data-active-state={isActive ? 'on' : 'off'}
      role='button'
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      aria-pressed={isActive}
      tooltip='Blockquote'
      onClick={handleClick}
      {...buttonProps}
      ref={ref}
    >
      {children ?? (
        <>
          <Icon className='tiptap-button-icon' />
          {text && <span className='tiptap-button-text'>{text}</span>}
          {showShortcut && (
            <BlockquoteShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  )
})

BlockquoteButton.displayName = 'BlockquoteButton'
BlockquoteButton.propTypes = {
  editor: PropTypes.object,
  text: PropTypes.string,
  hideWhenUnavailable: PropTypes.bool,
  onToggled: PropTypes.func,
  showShortcut: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
